import sessionStore from '../../../data/sessionPersistentStore'
import { createSlice } from '@reduxjs/toolkit'
import { createSelector } from '@reduxjs/toolkit'
import dayjs from 'dayjs'

/**
 * The initial state for the range of days payout feature.
 * 
 * @property {Object} initialState - The initial state object.
 * @property {Array|null} initialState.dates - An array representing the range of dates for payout.
 *   If the session store's range date payout is empty, it defaults to [null, null].
 *   Otherwise, it retrieves the range of dates from the session store.
 */
const initialState = {
  dates: sessionStore.isEmptyRangeDatePayout()
    ? [null, null]
    : sessionStore.getRangeDatePayout()
}

/**
 * Valida y convierte cualquier tipo de fecha válida a formato ISO.
 *
 * @param {any} date - La fecha a validar y convertir.
 * @param {string} defaultType - Tipo de ajuste ('startOf' o 'endOf') para fechas válidas.
 * @returns {string|null} La fecha en formato ISO o `null` si no es válida.
 */
const validateAndConvertToISO = (date, defaultType = 'startOf') => {
  if (dayjs(date).isValid()) {
    return dayjs(date)[defaultType]('day').toISOString()
  }
  return null
}

// Función para procesar el rango de fechas usando el validador genérico
/**
 * Processes a range of dates by validating and converting them to ISO format.
 * If the end date is invalid or not provided, it defaults to the current date's end of day in ISO format.
 *
 * @param {Array} dates - An array containing two date values. The first is the start date, and the second is the end date.
 * @param {string|Date} dates[0] - The start date to be validated and converted.
 * @param {string|Date} dates[1] - The end date to be validated and converted. Defaults to the current date's end of day if invalid.
 * @returns {Array<string>} An array containing two ISO-formatted date strings: the start date and the end date.
 */
const processDateRange = dates => [
  validateAndConvertToISO(dates[0], 'startOf'),
  validateAndConvertToISO(dates[1], 'endOf') ||
    dayjs().endOf('day').toISOString() // Usar fecha actual si no es válida
]

/**
 * A Redux slice for managing the range of days payout feature.
 *
 * @namespace rangeOfDaysPayoutSlice
 * @property {string} name - The name of the slice, used as a namespace in the Redux store.
 * @property {Object} initialState - The initial state of the slice.
 * @property {Object} reducers - The reducer functions for handling actions.
 *
 * @method addRangeOfDaysPayout
 * @description Adds a range of days for payout to the state and persists it in session storage.
 * @param {Object} state - The current state of the slice.
 * @param {Object} action - The dispatched action containing the payload.
 * @param {Array} action.payload - The range of dates to be processed and added to the state.
 *
 * @method resetRangeOfDaysPayout
 * @description Resets the range of days payout in the state and removes it from session storage.
 * @param {Object} state - The current state of the slice.
 */
const rangeOfDaysPayoutSlice = createSlice({
  name: 'datePayout',
  initialState,
  reducers: {
    addRangeOfDaysPayout(state, action) {
      const dates = action.payload
      const newDates = processDateRange(dates) // Usar flujo de funciones
      state.dates = newDates
      sessionStore.setRangeDatePayout(newDates)
    },
    resetRangeOfDaysPayout(state) {
      sessionStore.removeRangeDatePayout()
      state.dates = [null, null]
    }
  }
})

/**
 * Actions for managing the range of days payout state in the Redux slice.
 *
 * @property {Function} addRangeOfDaysPayout - Action to add a range of days payout.
 * @property {Function} resetRangeOfDaysPayout - Action to reset the range of days payout.
 */
export const { addRangeOfDaysPayout, resetRangeOfDaysPayout } =
  rangeOfDaysPayoutSlice.actions

/**
 * Selector function to retrieve the range of day payout state from the UI slice.
 *
 * @param {Object} state - The entire Redux state object.
 * @returns {*} The range of day payout state from the UI slice.
 */
const getDaySelector = state => state.ui.rangeOfDayPayout

/**
 * Selector to retrieve the current date payout range.
 *
 * This selector uses `getDaySelector` to access the state and maps over the `dates` array.
 * Each date is converted to a JavaScript Date object using `dayjs`. If a date is null or undefined,
 * it returns `null` for that entry. If the `dates` array is not present in the state, it defaults
 * to an array with two `null` values.
 *
 * @param {Object} state - The Redux state.
 * @returns {Array<Date|null>} An array containing two elements: the start and end dates as JavaScript Date objects, or `null` if not available.
 */
export const getCurrentDatePayout = createSelector(
  [getDaySelector],
  state =>
    state?.dates
      ? state.dates.map(date => (date ? dayjs(date).toDate() : null))
      : [null, null]
)

/**
 * Reducer function for the rangeOfDaysPayoutSlice.
 * This function is used to handle state transitions for the range of days payout feature.
 *
 * @type {import('@reduxjs/toolkit').Reducer}
 */
const reducer = rangeOfDaysPayoutSlice.reducer
/**
 * The default export of the module, which is a reducer function.
 * This reducer is responsible for managing the state related to the range of days payout feature.
 *
 * @module rangeOfDaysPayoutSlice
 * @function
 * @returns {Function} The reducer function to handle state updates.
 */
export default reducer
